import { useParams } from 'react-router';
import { useMutation } from '@apollo/client';
import { useSetRecoilState } from 'recoil';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { ALL_ITEMS } from '@/config/common.config';
import { DnrContractLines, DnrContractLinesVariables } from '@/graphql/contracts/_generated_/dnrContractLines.mutation';
import DnrContractLinesMutation from '@/graphql/contracts/dnrContractLines.mutation.gql';
import GetContractDetailsQuery from '@/graphql/contracts/getContractDetails.query.gql';
import { rightsidePanel_isOpen } from '../../../../RightPanel.state';
import { notificationService } from '@/services/notification/notification.client';
import {
  clearLinesSelection,
  useSelectedLines,
  triggerLineRefresh
} from '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/linesSelectionObservable';
import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';
import GetPaginatedFilteredVariableContractLinesQuery from
  '@/graphql/contracts/getPaginatedFilteredVariableContractLines.query.gql';
import { useSelectedVariableLines, triggerVariableRefresh } from
  '@/pages/ContractDetails/ContractLines/components/ContractLinesGrid/variableLinesSelectionObservable';

export type DnrContractLinesDto = {
  dnrDate: Date;
  reasonCode: string;
  comment: string;
  noFurtherBilling: boolean;
};

export default function useDnrContractLines() {
  const { contractId }: { contractId: UUID } = useParams();
  const selectedOrdinaryLineRows = useSelectedLines();
  const selectedVariableLineRows = useSelectedVariableLines();
  const selectedLines = selectedOrdinaryLineRows?.length ? selectedOrdinaryLineRows : selectedVariableLineRows;
  const setIsOpenRightPanel = useSetRecoilState(rightsidePanel_isOpen);
  const [_dnrContractLines, { loading, error }] = useMutation<DnrContractLines, DnrContractLinesVariables>(
    DnrContractLinesMutation,
    {
      client: apolloClientContracts,
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetContractDetailsQuery,
          variables: { contractId }
        },
        selectedOrdinaryLineRows.length ?
          {
            query: GetBCContractLinesQuery,
            variables: {
              filter: {
                contractId: { eq: contractId }
              },
              first: ALL_ITEMS
            }
          } :
          {
            query: GetPaginatedFilteredVariableContractLinesQuery,
            variables: {
              filter: { headerPrimaryKey: { eq: contractId } },
              first: ALL_ITEMS,
            }
          }
      ],
      onCompleted: () => {
        clearLinesSelection();
        setIsOpenRightPanel(false);
        triggerLineRefresh();
        triggerVariableRefresh();
        notificationService.open({
          title: 'Contract Line Status Saved',
          text: 'The chosen contract lines have been changed',
          type: 'success'
        });
      },
      onError: (err) => notificationService.open({
        title: 'Error',
        text: err.message,
        type: 'danger'
      }),
    }
  );

  const dnrContractLines = (params: DnrContractLinesDto) => {
    _dnrContractLines({
      variables: {
        ...params,
        contractId,
        contractLines: selectedLines.map(({ id }) => ({ contractLineID: id })),
      }
    });
  };

  return {
    dnrContractLines,
    isLoadingDNR: loading,
    errorDNR: error
  } as const;
}
